import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";
import { useProduct } from "@/pages/products/products.hooks";
import { useUsers } from "@/pages/users/users.hooks";

import {
  clientProductsFormSchema,
  clientProductsFormSchemaSearch,
  TClientProductsFormData,
  TClientProductsFormSearchData,
} from "./clients.schema";
import { TClient, TClientExtended } from "./clients.types";
import * as plansApi from "./plans.api";
import { IUsePlans } from "./plans.hooks.interface";
import { TPlanForBackend, TPlanUpdate } from "./plans.types";

export const usePlans = (
  selectedClient: TClient | TClientExtended | null
): IUsePlans => {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Hooks externos
  const { products } = useProduct();
  const { users } = useUsers();

  // Estados para gerenciamento de planos
  const [tabs, setTabs] = React.useState<{ id: string; label: string }[]>([]);
  const [newTabName, setNewTabName] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("tab1");

  // Estados para filtragem
  const [filteredProducts, setFilteredProducts] = React.useState(
    products || []
  );
  const [filteredUsers, setFilteredUsers] = React.useState(users || []);

  const clientProductsForm = useForm<TClientProductsFormData>({
    resolver: zodResolver(clientProductsFormSchema),
    defaultValues: {
      planos: [],
    },
  });

  const clientProductsFormSearch = useForm<TClientProductsFormSearchData>({
    resolver: zodResolver(clientProductsFormSchemaSearch),
    defaultValues: {
      product_name: "",
      member_name: "",
    },
  });

  // Funções de filtragem
  const handleSearchProducts = React.useCallback(() => {
    const searchTerm = clientProductsFormSearch
      .getValues("product_name")
      .toLowerCase()
      .trim();

    if (!searchTerm) {
      setFilteredProducts(products || []);
      return;
    }

    const filtered = (products || []).filter((product) =>
      product.name?.toLowerCase().includes(searchTerm)
    );
    setFilteredProducts(filtered);
  }, [products, clientProductsFormSearch]);

  const handleSearchUsers = React.useCallback(() => {
    const searchTerm = clientProductsFormSearch
      .getValues("member_name")
      .toLowerCase()
      .trim();

    if (!searchTerm) {
      setFilteredUsers(users || []);
      return;
    }

    const filtered = (users || []).filter((user) =>
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [users, clientProductsFormSearch]);

  const clearProductsFilter = React.useCallback(() => {
    setFilteredProducts(products || []);
  }, [products]);

  const clearUsersFilter = React.useCallback(() => {
    setFilteredUsers(users || []);
  }, [users]);

  // Funções para gerenciamento de planos
  const handleAddTab = React.useCallback((): { id: string; label: string } => {
    if (!newTabName.trim()) return { id: "", label: "" };

    const newId = `tab${tabs.length + 1}`;
    const newTab = { id: newId, label: newTabName };
    setTabs([...tabs, newTab]);
    setActiveTab(newId);
    setNewTabName("");
    return newTab;
  }, [newTabName, tabs]);

  const addTabToForm = React.useCallback(() => {
    if (!newTabName.trim()) return;
    const newTab = handleAddTab();
    if (!newTab.id) return;

    const currentPlanos = clientProductsForm.getValues("planos") || [];
    const newPlanoIndex = currentPlanos.length;

    const newPlano = {
      id: newTab.id,
      name: newTab.label,
      description: "",
      plan_features: [],
      plan_price: 0,
      products: [] as Array<{
        id: string;
        capital_value?: number;
        premium_value?: number;
        description?: string;
      }>,
      commission_distribution: [
        {
          agency: 0,
          commission: 0,
          position: 0,
          markup: 0,
          markup_mode: "percentage",
        },
      ],
    };

    clientProductsForm.setValue("planos", [...currentPlanos, newPlano], {
      shouldDirty: true,
      shouldTouch: true,
    });

    clientProductsForm.setValue(`planos.${newPlanoIndex}.name`, newTab.label, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [newTabName, handleAddTab, clientProductsForm]);

  const deleteTabFromForm = React.useCallback(
    (tabId: string) => {
      const index = tabs.findIndex((t) => t.id === tabId);
      setTabs(tabs.filter((tab) => tab.id !== tabId));

      if (index !== -1) {
        const currentPlanos = clientProductsForm.getValues("planos") || [];
        const newPlanos = [...currentPlanos];
        newPlanos.splice(index, 1);
        clientProductsForm.setValue("planos", newPlanos, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [tabs, clientProductsForm]
  );

  const handleUpdateTabLabel = React.useCallback(
    (tabId: string, newLabel: string) => {
      setTabs(
        tabs.map((tab) =>
          tab.id === tabId ? { ...tab, label: newLabel } : tab
        )
      );
    },
    [tabs]
  );

  // Atualizar produtos filtrados quando products mudar
  React.useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  // Atualizar usuários filtrados quando users mudar
  React.useEffect(() => {
    setFilteredUsers(users || []);
  }, [users]);

  const createPlanMutation = useMutation({
    mutationFn: (data: TPlanForBackend[]) => {
      return plansApi.createPlan(data, selectedClient?.id || "");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({
        queryKey: ["plans", selectedClient?.id],
      });
      toast({
        title: t("generic.success"),
        description: t("clients.success.planCreated"),
      });
      // Redireciona para a listagem de planos do cliente
      navigate(`/clients/${selectedClient?.id}/plans`);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async (payload: { planId: string; data: TPlanUpdate }) => {
      if (!selectedClient?.id) {
        throw new Error("Cliente inválido");
      }

      return plansApi.updatePlan(
        selectedClient.id,
        payload.planId,
        payload.data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.planSaved"),
      });
      navigate(`/clients/${selectedClient?.id}/plans`);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmitCreatePlan = React.useCallback(
    (data: TClientProductsFormData) => {
      if (Array.isArray(data.planos)) {
        const plansData: TPlanForBackend[] = data.planos.map((plano) => {
          return {
            organization_id: selectedClient?.id || "",
            name: plano.name || "",
            description: plano.description || "",
            products: (plano.products || []).map((product) => {
              return {
                product_id: product.product_id || product.id,
                name: product.name || "",
                type: product.type || "",
                capital_value: product.capital_value || 0,
                premium_value: product.premium_value || 0,
                description: product.description || "",
                insurance_policy_extra_price_id:
                  product.insurance_policy_extra_price_id || null,
              };
            }),
            commission_distribution: (plano.commission_distribution || []).map(
              (commission) => {
                const processedCommission = {
                  agency: Math.round((commission.agency || 0) * 100),
                  commission: Math.round((commission.commission || 0) * 100),
                  position: Math.round(commission.position || 0),
                };
                return processedCommission;
              }
            ),
            markup: Math.round(plano.markup || 0),
            markup_mode: plano.markup_mode || "percentage",
            commission_lifetime_other_installments: Math.round(
              (plano.commission_lifetime_other_installments || 0) * 100
            ),
            plan_price: plano.plan_price || 0,
            plan_features: Array.isArray(plano.plan_features)
              ? plano.plan_features
                  .flat()
                  .filter(
                    (feature) =>
                      typeof feature === "string" && feature.trim() !== ""
                  )
              : [],
          };
        });

        createPlanMutation.mutate(plansData as any);
      }
    },
    [createPlanMutation, selectedClient]
  );

  const handleSubmitUpdatePlan = React.useCallback(
    (index: number) => {
      const planos = clientProductsForm.getValues("planos") || [];
      const plano = planos[index];

      if (!plano || !plano.id) {
        return;
      }

      const processedFeatures = Array.isArray(plano.plan_features)
        ? plano.plan_features
            .flat()
            .filter(
              (feature) => typeof feature === "string" && feature.trim() !== ""
            )
        : [];

      const payload: TPlanUpdate = {
        name: plano.name || "",
        description: plano.description || "",
        products: (plano.products || []).map((p: any) => ({
          product_id: p.product_id || p.id,
          name: p.name || "",
          type: p.type || "",
          capital_value: p.capital_value || 0,
          premium_value: p.premium_value || 0,
          description: p.description || "",
          insurance_policy_extra_price_id:
            p.insurance_policy_extra_price_id || null,
        })),
        commission_distribution: (plano.commission_distribution || []).map(
          (c: any) => {
            const processedCommission = {
              agency: Math.round((c.agency || 0) * 100),
              commission: Math.round((c.commission || 0) * 100),
              position: Math.round(c.position || 0),
            };
            return processedCommission;
          }
        ),
        markup: Math.round(plano.markup || 0),
        markup_mode: plano.markup_mode || "percentage",
        commission_lifetime_other_installments: Math.round(
          (plano.commission_lifetime_other_installments || 0) * 100
        ),
        plan_price: plano.plan_price || 0,
        plan_features: processedFeatures,
      };

      updatePlanMutation.mutate({ planId: String(plano.id), data: payload });
    },
    [clientProductsForm, selectedClient, updatePlanMutation]
  );

  return {
    // Estados para gerenciamento de planos
    tabs,
    newTabName,
    activeTab,

    // Estados de filtragem
    filteredProducts,
    filteredUsers,

    // Formulários
    clientProductsForm,
    clientProductsFormSearch,

    // Funções de filtragem
    handleSearchProducts,
    handleSearchUsers,
    clearProductsFilter,
    clearUsersFilter,

    // Funções para gerenciamento de planos
    setNewTabName,
    setActiveTab,
    handleAddTab,
    addTabToForm,
    deleteTabFromForm,
    handleUpdateTabLabel,

    // Funções de criação de planos
    handleSubmitCreatePlan,
    handleSubmitUpdatePlan,

    // Dependências
    selectedClient,
    products: products || [],
    users: users || [],
  };
};
